import {
  type GetTourSessionsBreakdownArgs,
  type SessionsAsset,
  type TourActivity,
  useInfiniteScroll,
  useLazyGetTourSessionsBreakdownQuery,
} from '@carboncopyco/data';
import { TextCaption } from '@carboncopyco/ui';
import {
  formatPlural,
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect, useMemo, useState } from 'react';
import { SessionsBreakdownTable } from '../../../InfoPage/SessionsBreakdownTable';
import { useDashboardProps } from '../../DashboardContext';
import { ToursFilter } from '../../SessionBreakdownComponents';
import type { CsvParams } from '../../SessionBreakdownComponents/types';
import { hasProperties } from '../../utils';
import { toursBreakdownCols } from './cols';

export const ToursBreakdownTable = ({
  setCsvParams,
}: {
  setCsvParams: (params: CsvParams) => void;
}) => {
  const [tours, setTours] = useState<
    Record<SessionsAsset['assetId'], SessionsAsset['name']>
  >({});
  const [showAll, setShowAll] = useState(false);

  const {
    organizationId,
    selectedWorkspaceIds,
    timeRange,
    short,
    internal,
    tourPagePath,
  } = useDashboardProps();

  const fetchArgs = useMemo<GetTourSessionsBreakdownArgs>(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      tourIds: Object.keys(tours),
    }),
    [internal, organizationId, short, timeRange, selectedWorkspaceIds, tours]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScroll(useLazyGetTourSessionsBreakdownQuery);

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchFirstBatch, fetchArgs]);

  const clearFilters = () => {
    setTours({});
  };

  useEffect(() => {
    setCsvParams({
      params: {
        ...fetchArgs,
        resourceTypes: ['tour'],
      },
      isDisabled: (items && !items.length) || !!error,
    });
  }, [fetchArgs, items, error, setCsvParams]);

  return (
    <>
      <div className="w-full flex items-center justify-between px-6 py-5">
        <ToursFilter
          tours={tours}
          setTours={setTours}
        />
        {!!items?.length && (
          <TextCaption
            color="text-ds-natural-500"
            label={`${
              totalSize > 7 && !showAll ? 7 : items.length
            } ${formatPlural('session', items.length)} (out of ${totalSize})`}
          />
        )}
      </div>
      <SessionsBreakdownTable<TourActivity>
        showAll={showAll}
        setShowAll={setShowAll}
        totalSize={totalSize}
        noResults={hasProperties(tours) && !items.length}
        onClear={clearFilters}
        cols={toursBreakdownCols(tourPagePath)}
        data={items}
        isLoading={isLoading}
        isLoadingBatch={isLoadingBatch}
        error={error}
        handleFetchNextBatch={() => fetchNextBatch(fetchArgs)}
      />
    </>
  );
};
